import React, { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  History,
  ListOrdered,
  Pencil,
  Plus,
  Layers,
  ShieldCheck,
} from 'lucide-react'
import MessengerLayout from '../../../layout/MessengerLayout'
import '../../../components/cases/cases.css'
import {
  caseTypes as fetchCaseTypes,
  createCaseType,
  previewCaseTypeUpdate,
  updateCaseType,
} from '../../../components/admin/cases/adminApi'
import { NOT_ENTITLED } from '../../../components/cases/api'
import StageEditor, {
  TimingFields,
  newStage,
  slugFromLabel,
} from '../../../components/admin/cases/StageEditor'
import MigrationPrompt from '../../../components/admin/cases/MigrationPrompt'
import RoleBuilder from '../../../components/admin/cases/RoleBuilder'
import {
  Button,
  Chip,
  Empty,
  Field,
  Modal,
  Notice,
  Panel,
  Refusal,
  Spinner,
} from '../../../components/cases/ui'
import { date, pluralise } from '../../../components/cases/format'

// ======================================================================
// CASE TYPE SETTINGS
// ======================================================================
//
// The tenant's own procedures: what stages a disciplinary or a
// grievance goes through, how long each one should take, when the case
// owner gets chased, and how long the file is kept afterwards.
//
// ======================================================================
// THIS SCREEN IS DELIBERATELY NOT BEHIND STEP-UP
// ======================================================================
//
// The org case list is; this is not, and the difference is the point.
// Step-up proves the person at the keyboard is the account holder,
// which is the right question before showing them a list of colleagues
// under investigation and the wrong one before editing a list of stage
// names. Nothing on this screen names an employee, a case or an
// allegation — the closest it comes is a case REFERENCE in the
// migration prompt, and only for cases the administrator's own edit
// could not carry.
//
// Requiring an MFA challenge here would train administrators to clear
// step-up prompts for routine configuration, which is how a step-up
// prompt stops meaning anything on the surfaces where it matters. The
// backend agrees and enforces it: er_cases/admin_routes.py attaches
// require_stepup to the case list route alone.
//
// ======================================================================
// EDITING NEVER MUTATES A VERSION
// ======================================================================
//
// Every save writes a NEW version (task 22). The old one is immutable
// at the database — a trigger refuses the UPDATE — and every case
// pinned to it goes on running under it until somebody deliberately
// moves it. So this screen never asks "are you sure you want to change
// this", because nothing is being changed; it asks the only question
// that has consequences, which is what should happen to the cases
// already running. See MigrationPrompt.

export default function CaseTypeSettings() {
  const [types, setTypes] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [editing, setEditing] = useState(null) // CaseTypeOut | 'new' | null

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    return fetchCaseTypes()
      .then((res) => setTypes(res === NOT_ENTITLED ? [] : res || []))
      .catch(setError)
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    load()
  }, [load])

  return (
    <MessengerLayout>
      <div className="cases-root min-h-screen">
        {/* FULL WIDTH (task 26), matching the org case list it links
            to. The stage editor rows benefit directly — the label field
            and the target sit on one line at any width instead of
            wrapping. */}
        <div className="w-full px-5 py-6 sm:px-8">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="eyebrow">Case administration</div>
              <h1 className="mt-1 font-head text-[22px] font-bold leading-tight text-ink">
                Case types
              </h1>
            </div>
            <div className="flex items-center gap-2">
              <Link to="/admin/cases" className="btn">
                <Layers size={14} strokeWidth={1.9} />
                Organisation cases
              </Link>
              <Button variant="primary" icon={Plus} onClick={() => setEditing('new')}>
                New case type
              </Button>
            </div>
          </div>

          <div className="mt-5">
            <Notice tone="accent" icon={ShieldCheck} title="Editing is always safe">
              A case type is versioned. Saving an edit writes a new version and
              leaves the old one exactly as it was, so a case that is already
              running keeps the process it was raised under until you choose to
              move it. Nothing you do here can change a case in flight without
              being asked first.
            </Notice>
          </div>

          {error && (
            <div className="mt-4">
              <Refusal error={error} onDismiss={() => setError(null)} />
            </div>
          )}

          <div className="mt-4 space-y-3">
            {loading && !types ? (
              <Panel>
                <Spinner label="Loading your organisation’s procedures" />
              </Panel>
            ) : (types || []).length === 0 ? (
              <Panel>
                <Empty icon={ListOrdered} title="No case types configured yet">
                  A case type is the procedure a case follows — its stages, its
                  timescales and how long the file is kept. Nobody can open a
                  case until at least one exists.
                </Empty>
              </Panel>
            ) : (
              (types || []).map((t) => (
                <TypeCard key={t.case_type_id} type={t} onEdit={() => setEditing(t)} />
              ))
            )}
          </div>

          {/* RENAMING THE NINE CASE ROLES (task 27 part C3).
              BESIDE THE CASE TYPES RATHER THAN ON ITS OWN SCREEN. Both
              are the same kind of thing — the tenant's vocabulary for
              its own process — and an administrator setting up the
              module wants the stages and the role names in one sitting.
              A separate page would also need its own route, its own
              gate and its own entry in the navigation, for one list. */}
          <div className="mt-8 border-t border-line pt-6">
            <div className="mb-4">
              <div className="eyebrow">Vocabulary</div>
              <h2 className="mt-1 font-head text-[17px] font-bold leading-tight text-ink">
                Case roles
              </h2>
            </div>
            <RoleBuilder />
          </div>
        </div>

        {editing && (
          <EditorModal
            type={editing === 'new' ? null : editing}
            onClose={() => setEditing(null)}
            onSaved={() => {
              setEditing(null)
              load()
            }}
          />
        )}
      </div>
    </MessengerLayout>
  )
}

// ======================================================================
// ONE CASE TYPE, AS THE LIST SHOWS IT
// ======================================================================

function TypeCard({ type, onEdit }) {
  const v = type.current_version
  return (
    <Panel
      eyebrow={`Version ${v.version_no}`}
      title={type.label}
      right={
        <div className="flex items-center gap-2">
          {!type.is_active && <Chip>Retired</Chip>}
          <Button size="sm" icon={Pencil} onClick={onEdit}>
            Edit
          </Button>
        </div>
      }
    >
      <div className="flex flex-wrap items-center gap-1.5">
        {v.stages.length === 0 ? (
          <Chip tone="danger">No usable stages</Chip>
        ) : (
          v.stages.map((s) => (
            <Chip key={s.key} title={`${s.target_days} working days`}>
              {s.label}
            </Chip>
          ))
        )}
      </div>

      <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 text-[12px] sm:grid-cols-4">
        <Stat label="Stages" value={String(v.stages.length)} />
        <Stat
          label="Reminders"
          value={
            v.reminder_day == null
              ? 'None'
              : `After ${pluralise(v.reminder_day, 'working day', 'working days')}`
          }
        />
        <Stat label="Retention" value={pluralise(v.retention_years, 'year', 'years')} />
        <Stat
          label="Cases"
          value={`${type.open_case_count} open, ${type.total_case_count} total`}
        />
      </dl>

      {type.versions.length > 1 && (
        <p className="mt-3 flex items-center gap-1.5 text-[11.5px] text-faint">
          <History size={12} strokeWidth={1.9} />
          {pluralise(type.versions.length, 'version', 'versions')} in total. Older
          versions are kept because cases raised under them are still running on
          them — the earliest is from {date(type.versions[type.versions.length - 1].created_at)}.
        </p>
      )}
    </Panel>
  )
}

function Stat({ label, value }) {
  return (
    <div>
      <dt className="eyebrow">{label}</dt>
      <dd className="mt-0.5 text-muted">{value}</dd>
    </div>
  )
}

// ======================================================================
// THE EDITOR
// ======================================================================

const DEFAULT_STAGES = [
  { key: 'intake', label: 'Intake', target_days: 3 },
  { key: 'investigation', label: 'Investigation', target_days: 10 },
  { key: 'hearing', label: 'Hearing', target_days: 5 },
  { key: 'outcome', label: 'Outcome', target_days: 3 },
]
/** A STARTING POINT, NOT A DEFAULT PROCEDURE. It is offered only when
 *  creating from scratch, every field is editable before saving, and
 *  nothing in this application assumes these keys exist — migration 053
 *  deliberately put no CHECK on er_cases.current_stage so a tenant names
 *  its own stages. A blank form would be worse: it invites somebody to
 *  invent a disciplinary procedure from an empty box. */

function EditorModal({ type, onClose, onSaved }) {
  const isNew = !type
  const current = type?.current_version

  const [label, setLabel] = useState(type?.label || '')
  const [stages, setStages] = useState(
    current
      ? current.stages.map((s) => ({ ...s }))
      : DEFAULT_STAGES.map((s) => ({ ...s })),
  )
  const [timing, setTiming] = useState({
    target_days: current?.target_days ?? 20,
    reminder_day: current?.reminder_day ?? null,
    escalate_to_role: current?.escalate_to_role ?? null,
    retention_years: current?.retention_years ?? 6,
  })
  const [isActive, setIsActive] = useState(type?.is_active ?? true)

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  // The migration flow. Only ever entered on an EDIT: a new case type
  // has no cases yet, so there is nothing to ask about.
  const [prompt, setPrompt] = useState(false)
  const [preview, setPreview] = useState(null)
  const [previewError, setPreviewError] = useState(null)

  const cleanStages = () =>
    stages
      .map((s) => ({
        key: s.key,
        label: String(s.label || '').trim(),
        target_days: s.target_days === '' ? null : s.target_days,
      }))
      // A stage the user emptied is dropped rather than sent as a blank,
      // which parse_stages would refuse with a message about stages[3]
      // that means nothing to the person reading it.
      .filter((s) => s.label !== '')

  const save = async () => {
    setError(null)

    if (isNew) {
      setBusy(true)
      try {
        await createCaseType({
          // THE TYPE'S KEY IS DERIVED FROM THE LABEL AND NEVER SHOWN AS
          // AN EDITABLE FIELD, for the same reason a stage key is not:
          // er_cases.case_type_id pins a version, and create_case()
          // resolves a type by this key. It is generated once, here, and
          // is thereafter fixed.
          key: slugFromLabel(label),
          label: label.trim(),
          stages: cleanStages(),
          target_days: timing.target_days === '' ? null : timing.target_days,
          reminder_day: timing.reminder_day,
          escalate_to_role: timing.escalate_to_role,
          retention_years:
            timing.retention_years === '' ? null : timing.retention_years,
        })
        onSaved()
      } catch (e) {
        setError(e)
      } finally {
        setBusy(false)
      }
      return
    }

    // AN EDIT ALWAYS GOES THROUGH THE PREVIEW FIRST. Not "when the
    // stages changed" — deciding that here would mean comparing two
    // stage arrays for semantic equality and being right about it, and
    // being wrong means an administrator who believes they were asked
    // and was not. The preview writes nothing, so the cost of always
    // running it is one request.
    setPrompt(true)
    setPreview(null)
    setPreviewError(null)
    try {
      const res = await previewCaseTypeUpdate(type.key, cleanStages())
      setPreview(res)
    } catch (e) {
      setPreviewError(e)
    }
  }

  const confirm = async (applyToOpenCases) => {
    setBusy(true)
    setError(null)
    try {
      await updateCaseType(type.key, {
        label: label.trim(),
        stages: cleanStages(),
        target_days: timing.target_days === '' ? null : timing.target_days,
        reminder_day: timing.reminder_day,
        escalate_to_role: timing.escalate_to_role,
        retention_years:
          timing.retention_years === '' ? null : timing.retention_years,
        is_active: isActive,
        apply_to_open_cases: applyToOpenCases,
      })
      setPrompt(false)
      onSaved()
    } catch (e) {
      setPrompt(false)
      setError(e)
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <Modal
        open={!prompt}
        onClose={busy ? () => {} : onClose}
        width="max-w-3xl"
        title={isNew ? 'New case type' : `Edit ${type.label}`}
        subtitle={
          isNew
            ? 'The procedure a case will follow: its stages, in order, and how long each should take.'
            : `Saving writes version ${current.version_no + 1}. Version ${current.version_no} stays exactly as it is, for the cases already running on it.`
        }
      >
        {error && (
          <div className="mb-4">
            <Refusal error={error} onDismiss={() => setError(null)} />
          </div>
        )}

        <div className="space-y-5">
          <Field
            label="Name"
            required
            hint="What everyone opening or reading a case sees. Safe to change at any time — cases do not record it."
          >
            <input
              className="field"
              maxLength={80}
              value={label}
              disabled={busy}
              placeholder="Disciplinary"
              onChange={(e) => setLabel(e.target.value)}
            />
          </Field>

          <div className="border-t border-line pt-4">
            <StageEditor stages={stages} onChange={setStages} disabled={busy} />
          </div>

          <div className="border-t border-line pt-4">
            <div className="eyebrow mb-2">Timescales and handling</div>
            <TimingFields value={timing} onChange={setTiming} disabled={busy} />
          </div>

          {!isNew && (
            <div className="border-t border-line pt-4">
              <label className="flex cursor-pointer items-start gap-3">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={!isActive}
                  disabled={busy}
                  onChange={(e) => setIsActive(!e.target.checked)}
                />
                <span>
                  <span className="block font-head text-[12.5px] font-semibold text-ink">
                    Retire this case type
                  </span>
                  <span className="mt-0.5 block text-[12px] leading-relaxed text-faint">
                    Removes it from the list of procedures a new case can be
                    opened on. Cases already running on it are untouched and
                    carry on exactly as they are — retiring a procedure never
                    closes, moves or hides a case.
                  </span>
                </span>
              </label>
            </div>
          )}
        </div>

        <footer className="mt-5 flex items-center justify-end gap-2 border-t border-line pt-4">
          <Button variant="ghost" disabled={busy} onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            busy={busy}
            disabled={!label.trim() || stages.length === 0}
            onClick={save}
          >
            {isNew ? 'Create case type' : 'Save changes'}
          </Button>
        </footer>
      </Modal>

      <MigrationPrompt
        open={prompt}
        onClose={() => {
          setPrompt(false)
          setPreview(null)
          setPreviewError(null)
        }}
        typeLabel={type?.label}
        nextVersionNo={(current?.version_no ?? 0) + 1}
        preview={preview}
        previewError={previewError}
        busy={busy}
        onConfirm={confirm}
      />
    </>
  )
}

// Referenced by the stage editor's "add" path so a brand-new type and a
// brand-new stage produce keys the same way. Re-exported here only so
// this module's imports read as one place; the implementation is
// StageEditor's.
export { newStage }
